import { Fragment, useContext, useState } from "react";
import { MdBlock, MdCheckCircle, MdDelete } from "react-icons/md";
import { NotifyContext } from "../../../context/notifyContext";
import UserAPI from "../../../services/api/UserService";
import { Parent, UserStatus } from "../../../Types/Users";
import ButtonLoading from "../../../UI/Button/ButtonLoading";
import Modal from "../../../UI/Modal/Modal";
import AcceptParent from "./AcceptParent";
import RejectParent from "./RejectParent";
import DeleteParent from "./DeleteParent";
import UserTabs from "./UserTabs";

type ParentDetailsProps = {
  parent: Parent;
  fetchData: () => void;
};

const permissions = [
  {
    tag: "Blog",
    permissions: [
      { title: "Comment a blog", code: "create_blog_comments" },
    ],
  },
  {
    tag: "Community",
    permissions: [
      { title: "View community requests", code: "view_community_requests" },
      { title: "Create community requests", code: "create_community_requests" },
      { title: "Edit community requests", code: "edit_community_requests" },
      { title: "Delete community requests", code: "delete_community_requests" },
      { title: "Comment a community", code: "create_forum_comments" },
    ],
  },
  {
    tag: "Students",
    permissions: [
      { title: "View student profiles", code: "view_student_profiles" },
      { title: "Create student profiles", code: "create_student_profiles" },
      { title: "Edit student profiles", code: "edit_student_profiles" },
    ],
  },
  {
    tag: "Tutors",
    permissions: [
      { title: "View tutor profiles", code: "view_tutor_profiles" },
    ],
  },
];

export default function ParentDetails({
  parent,
  fetchData,
}: ParentDetailsProps) {
  const [modal_confirm, setModalConfirm] = useState<{
    open: boolean;
  }>({ open: false });

  const [modal_reject, setModalReject] = useState<{
    open: boolean;
  }>({ open: false });

  const [modal_delete, setModalDelete] = useState<{
    open: boolean;
  }>({ open: false });

  const [userPermissions, setUserPermissions] = useState(
    parent?.permissions || []
  );

  const notify = useContext(NotifyContext);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleCheckboxChange = (tag: string, code: string) => {
    const updatedPermissions = [...userPermissions];
    const permissionIndex = updatedPermissions.findIndex(
      (permission) => permission === code
    );

    if (permissionIndex > -1) {
      updatedPermissions.splice(permissionIndex, 1);
    } else {
      updatedPermissions.push(code);
    }

    setUserPermissions(updatedPermissions);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    UserAPI.update_user_permissions(parent.user_id, userPermissions)
      .then((data) => {
        notify("Mise à jour des permissions effectuée.");
        setError("");
        fetchData();
      })
      .catch((error) => {
        setError(error);
        notify(error, "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!parent) return <></>;
  return (
    <Fragment>
      <Modal
        open={modal_confirm.open}
        title={`Confirmer ${parent.first_name} ${parent.last_name}`}
        onClose={() => {
          setModalConfirm({ open: false });
        }}
      >
        <AcceptParent
          onClose={() => {
            fetchData();
            setModalConfirm({ open: false });
          }}
          parent={parent}
        />
      </Modal>

      <Modal
        open={modal_reject.open}
        title={`Rejeter ${parent.first_name} ${parent.last_name}`}
        onClose={() => {
          setModalReject({ open: false });
        }}
      >
        <RejectParent
          onClose={() => {
            fetchData();
            setModalReject({ open: false });
          }}
          parent={parent}
        />
      </Modal>

      <Modal
        open={modal_delete.open}
        title={`Supprimer ${parent.first_name} ${parent.last_name}`}
        onClose={() => {
          setModalDelete({ open: false });
        }}
      >
        <DeleteParent
          onClose={() => {
            fetchData();
            setModalDelete({ open: false });
          }}
          parent={parent}
        />
      </Modal>

      <div className="container mx-auto my-5 p-5">
        <div className="md:flex no-wrap md:-mx-2 ">
          {/* <!-- Left Side --> */}
          <div className="w-full md:w-3/12 md:mx-2">
            {/* <!-- Profile Card --> */}
            <div className="bg-white p-3 border-t-4 border-green-400">
              <div className="image overflow-hidden">
                <img
                  className="h-auto w-full mx-auto"
                  src="https://lavinephotography.com.au/wp-content/uploads/2017/01/PROFILE-Photography-112.jpg"
                  alt=""
                />
              </div>
              <h1 className="text-gray-900 font-bold text-xl leading-8 my-1">
                {parent.first_name} {parent.last_name}
              </h1>
              
              <ul className="bg-gray-100 text-gray-600 hover:text-gray-700 hover:shadow py-2 px-3 mt-5 divide-y rounded shadow-sm">
                
                <li className="flex items-center py-3">
                  <span>Statut</span>

                  <span className="ml-auto">
                    {parent.user_status === UserStatus.PENDING && (
                      <span className="bg-green-500 py-1 px-2 rounded text-white text-sm">
                        En attente
                      </span>
                    )}

                    {parent.user_status === UserStatus.VALIDATED && (
                      <span className="bg-green-500 py-1 px-2 rounded text-white text-sm">
                        Validé
                      </span>
                    )}

                    {parent.user_status === UserStatus.BLOCKED && (
                      <span className="bg-red-500 py-1 px-2 rounded text-white text-sm">
                        Bloqué
                      </span>
                    )}

                    {parent.user_status === UserStatus.REFUSED && (
                      <span className="bg-red-500 py-1 px-2 rounded text-white text-sm">
                        Refusé
                      </span>
                    )}

                    {parent.user_status === UserStatus.CONFIRMED && (
                      <span className="bg-gray-500-500 py-1 px-2 rounded text-white text-sm">
                        En attente
                      </span>
                    )}
                  </span>
                </li>
                <li className="flex items-center py-3">
                  <span>Ajouté le</span>
                  <span className="ml-auto">
                    {parent.created_at ? new Date(parent.created_at).toDateString() : "-"}
                  </span>
                </li>
              </ul>
            </div>
            {/* <!-- End of profile card --> */}
            <div className="my-4"></div>
          </div>
          {/* <!-- Right Side --> */}
          <div className="w-full md:w-9/12 mx-2 h-64">
            {/* <!-- Profile tab --> */}
            {/* <!-- About Section --> */}
            <div className="bg-white p-3 shadow-sm rounded-sm">
              <div className="flex items-center space-x-2 font-semibold text-gray-900 leading-8">
                <span className="text-green-500">
                  <svg
                    className="h-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </span>
                <span className="tracking-wide">À propos du parent</span>
              </div>
              <div className="text-gray-700">
                <div className="grid md:grid-cols-2 text-sm">
                  <div className="grid grid-cols-2">
                    <div className="px-4 py-2 font-semibold">Nom</div>
                    <div className="px-4 py-2">{parent.first_name}</div>
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="px-4 py-2 font-semibold">Prénom</div>
                    <div className="px-4 py-2">{parent.last_name}</div>
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="px-4 py-2 font-semibold">Genre</div>
                    <div className="px-4 py-2">
                      {parent.civility === "M" ? "Homme" : "Femme"}
                    </div>
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="px-4 py-2 font-semibold">Contact No.</div>
                    <div className="px-4 py-2">{parent.phone_number}</div>
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="px-4 py-2 font-semibold">Adresse</div>
                    <div className="px-4 py-2">{parent.address_line_1}</div>
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="px-4 py-2 font-semibold">Email.</div>
                    {parent.is_email_verified ? (
                      <div className="flex gap-2 items-center px-4 py-2">
                        <a
                          className="text-blue-800"
                          href={`mailto:${parent.email}`}
                        >
                          {parent.email}
                        </a>
                        <MdCheckCircle color="green" className="text-lg" />
                      </div>
                    ) : (
                      <div className="flex gap-2 items-center px-4 py-2">
                        <a
                          className="text-blue-800"
                          href={`mailto:${parent.email}`}
                        >
                          {parent.email}
                        </a>
                        <MdBlock
                          title="Email non confirmé"
                          color="red"
                          className="text-lg"
                        />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2">
                    <div className="px-4 py-2 font-semibold">
                      Date de naissance
                    </div>
                    <div className="px-4 py-2">
                      {parent.birth_date ? new Date(parent.birth_date).toLocaleDateString() : "-"}
                    </div>
                  </div>
                </div>
              </div>
              {(parent.user_status === UserStatus.PENDING ||
                parent.user_status === UserStatus.CONFIRMED) && (
                  <div className="flex justify-end gap-4">
                    <button
                      className="block  bg-green-600 text-white text-sm font-semibold rounded-lg hover:opacity-60 focus:outline-none focus:shadow-outline focus:opacity-60 hover:shadow-xs p-3 my-4"
                      onClick={() => setModalConfirm({ open: true })}
                    >
                      Confirmer le parent
                    </button>
                    <button
                      className="block  bg-red-600 text-white text-sm font-semibold rounded-lg hover:opacity-60 focus:outline-none focus:shadow-outline focus:opacity-60 hover:shadow-xs p-3 my-4"
                      onClick={() => setModalReject({ open: true })}
                    >
                      Rejeter la demande
                    </button>
                  </div>
                )}
              {parent.user_status === UserStatus.REFUSED && (
                <div className="flex justify-end gap-4">
                  <button
                    className="flex items-center gap-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:opacity-60 focus:outline-none focus:shadow-outline focus:opacity-60 hover:shadow-xs p-3 my-4"
                    onClick={() => setModalDelete({ open: true })}
                  >
                    <MdDelete className="text-lg" />
                    Supprimer le profil
                  </button>
                </div>
              )}
            </div>
            {/* <!-- End of about section --> */}

            <div className="my-4"></div>

            {/* <!-- Permissions Section --> */}
            <div className="bg-white p-3 shadow-sm rounded-sm">
              <div className="flex items-center space-x-2 font-semibold text-gray-900 leading-8">
                <span className="text-green-500">
                  <svg
                    className="h-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </span>
                <span className="tracking-wide">Permissions du parent</span>
              </div>
              <div className="text-gray-700">
                <form className="mt-4 mb-8" onSubmit={handleSubmit}>
                  {permissions.map((group) => (
                    <div key={group.tag} className="mb-4">
                      <h2 className="text-base font-semibold mb-2">
                        {group.tag}
                      </h2>
                      <div className="grid grid-cols-3 gap-4">
                        {group.permissions.map((permission) => (
                          <label
                            key={permission.code}
                            className="flex items-center text-sm"
                          >
                            <input
                              type="checkbox"
                              className="form-checkbox mr-2"
                              checked={userPermissions.some(
                                (p) => p === permission.code
                              )}
                              onChange={() =>
                                handleCheckboxChange(group.tag, permission.code)
                              }
                            />
                            {permission.title}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  <ButtonLoading
                    type="submit"
                    loading={loading}
                    label="Save permissions"
                    className="mt-4"
                  />
                </form>
              </div>
            </div>
            {/* <!-- End of permissions --> */}

            <div className="my-4"></div>

            {/* User Tabs - Sessions, Orders, Transactions, Stats */}
            <UserTabs userId={parent.user_id} />

            <div className="my-4"></div>

          </div>
        </div>
      </div>
    </Fragment>
  );
}
